import Phaser from 'phaser';
import Enemy, { EnemyWorld } from '../objects/Enemy';
import EnemyBullet from '../objects/EnemyBullet';
import Potion from '../objects/Potion';
import { BOSS_ENEMY, pickEnemyForFloor } from '../enemies';
import * as Bal from '../balance';
import { potionDropChance } from '../rewards';
import { DEPTH, TINT } from '../ui/theme';
import type DungeonBuilder from './DungeonBuilder';

/** 刷怪时的避让参数（单一来源） */
const SPAWN_MIN_PLAYER_DIST = 200;

export interface EnemyDirectorDeps {
  enemies: Phaser.Physics.Arcade.Group;
  enemyBullets: Phaser.Physics.Arcade.Group;
  potions: Phaser.Physics.Arcade.Group;
  player: Phaser.Physics.Arcade.Sprite & { takeDamage(a: number): boolean };
  builder: DungeonBuilder;
  getFloor: () => number;
  getPlayerPosition: () => { x: number; y: number };
}

/**
 * 敌人调度器：刷怪、敌方子弹、爆炸/斩击/召唤特效、追踪弹制导。
 * 同时向 Enemy 暴露 world 回调（EnemyWorld）。
 */
export default class EnemyDirector {
  /** 传给 Enemy 的世界回调（单一实例，避免每只怪各建一份） */
  readonly world: EnemyWorld;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly deps: EnemyDirectorDeps
  ) {
    this.world = {
      player: deps.player,
      fireBullet: (x, y, angle, speed, damage, kind, life, explodeRadius, tint) =>
        this.fire(x, y, angle, speed, damage, kind, life, explodeRadius, tint),
      explode: (x, y, radius, damage) => this.explode(x, y, radius, damage),
      summon: (id, x, y) => this.spawn(id, x, y),
      slash: (x, y, angle, reach, tint) => this.slash(x, y, angle, reach, tint),
    };
  }

  /** 刷一整波（按楼层解锁的怪池，每 BOSS_EVERY 层首只为 BOSS） */
  spawnWave(count: number) {
    const floor = this.deps.getFloor();
    const bossFloor = floor % Bal.BOSS_EVERY === 0;
    const eliteChance = Bal.eliteChance(floor);
    const pp = this.deps.getPlayerPosition();

    // 候选刷新点：只在「开放格」中心生成（优先四邻皆开放，
    // 以容纳比一格还大的怪物碰撞体）；并排除墙/门/宝箱/药水占用格。
    // → 从源头保证怪物绝不刷新在墙内。
    const blocked = this.deps.builder.blockedTiles();
    const isFree = (t: { x: number; y: number }) =>
      !blocked.has(this.deps.builder.tileKey(Math.floor(t.x / Bal.TILE), Math.floor(t.y / Bal.TILE)));
    let pool = this.deps.builder.spawnTiles(true).filter(isFree);
    if (pool.length === 0) pool = this.deps.builder.spawnTiles(false).filter(isFree);

    // 打乱候选，逐个取用并移除，避免多只怪堆在同一格
    const shuffled = [...pool];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    for (let i = 0; i < count; i++) {
      const boss = bossFloor && i === 0;

      // 优先选远离玩家的格子
      let idx = shuffled.findIndex(
        (t) => Phaser.Math.Distance.Between(t.x, t.y, pp.x, pp.y) >= SPAWN_MIN_PLAYER_DIST
      );
      if (idx < 0) idx = shuffled.length > 0 ? 0 : -1;

      let x: number;
      let y: number;
      if (idx >= 0) {
        const pick = shuffled.splice(idx, 1)[0];
        x = pick.x;
        y = pick.y;
      } else {
        // 极端兜底（地图必有房间，正常不会走到）
        x = this.deps.builder.worldWidth / 2;
        y = this.deps.builder.worldHeight / 2;
      }

      const elite = !boss && Math.random() < eliteChance;
      const id = boss ? BOSS_ENEMY.id : pickEnemyForFloor(floor).id;

      // 58 层后词缀
      const affix = !boss && !elite && Math.random() < Bal.affixChance(floor)
        ? Bal.randomAffix()
        : undefined;

      this.spawn(id, x, y, { elite, boss, affix });
    }
  }

  /**
   * 生成单只怪（也用于召唤）。位置统一吸附到最近的开放格中心，
   * 确保不会卡在墙内（召回到墙里的小怪会被自动挪到最近的空地）。
   */
  spawn(id: string, x: number, y: number, ctx: { elite?: boolean; boss?: boolean; affix?: Bal.AffixDef } = {}): Enemy {
    const pos = this.deps.builder.snapToOpenTile(x, y);
    const e = new Enemy(this.scene, pos.x, pos.y, id, this.world, {
      floor: this.deps.getFloor(),
      ...ctx,
    });
    this.deps.enemies.add(e);
    return e;
  }

  /** 击杀掉落的药水（金币由调用方按 rewards.coinDropCount 处理） */
  dropPotionIfAny(enemy: Enemy) {
    if (Math.random() < potionDropChance(enemy)) {
      this.deps.potions.add(new Potion(this.scene, enemy.x, enemy.y, 'potion'));
    }
  }

  // ── 世界特效 / 攻击 ───────────────────────────────────────

  fire(
    x: number, y: number, angle: number, speed: number, damage: number,
    kind: 'straight' | 'homing' | 'lob', life: number, explodeRadius: number, tint: number
  ) {
    const b = this.deps.enemyBullets.get(x, y, 'bullet_enemy') as EnemyBullet | null;
    if (!b) return;
    b.launch(x, y, angle, speed, damage, kind, life, explodeRadius, tint, 'bullet_enemy');
    b.onExpire = () => {
      if (b.active && b.willExplode) this.explode(b.x, b.y, b.explodeRadius, b.damage);
      b.deactivate();
    };
  }

  /** 爆炸：环状特效 + 范围内伤害玩家 */
  explode(x: number, y: number, radius: number, damage: number) {
    const ring = this.scene.add
      .image(x, y, 'fx-ring').setDepth(DEPTH.fx).setTint(TINT.fxRing).setScale(radius / 26);
    this.scene.tweens.add({
      targets: ring, scale: (radius / 26) * 1.6, alpha: 0,
      duration: 330, onComplete: () => ring.destroy(),
    });
    const spark = this.scene.add
      .image(x, y, 'fx-spark').setDepth(DEPTH.fxSpark).setTint(TINT.fxSpark).setScale(radius / 7);
    this.scene.tweens.add({
      targets: spark, alpha: 0, scale: (radius / 7) * 0.4,
      duration: 260, onComplete: () => spark.destroy(),
    });

    const p = this.deps.player;
    if (Phaser.Math.Distance.Between(x, y, p.x, p.y) <= radius + 10) {
      p.takeDamage(damage);
      this.scene.cameras.main.shake(130, 0.007);
    }
  }

  /** 斩击/冲击特效（近战、冲锋、召唤） */
  slash(x: number, y: number, angle: number, reach: number, tint: number) {
    const s = this.scene.add
      .image(x, y, 'fx-slash').setDepth(DEPTH.fx).setTint(tint).setRotation(angle);
    s.setScale(reach / 22);
    this.scene.tweens.add({
      targets: s, alpha: 0, scale: (reach / 22) * 1.3,
      duration: 190, onComplete: () => s.destroy(),
    });
  }

  /** 召唤小怪（带出生特效）；坐标自动吸附到最近的开放格 */
  summon(id: string, x: number, y: number) {
    const pos = this.deps.builder.snapToOpenTile(x, y);
    const puff = this.scene.add
      .image(pos.x, pos.y, 'fx-spark').setDepth(DEPTH.fx - 1).setTint(TINT.fxSpawn);
    this.scene.tweens.add({
      targets: puff, alpha: 0, scale: 2.2,
      duration: 300, onComplete: () => puff.destroy(),
    });
    this.spawn(id, pos.x, pos.y);
  }

  // ── 子弹碰撞回调 ──────────────────────────────────────────

  hitWall(b: EnemyBullet) {
    if (!b.active) return;
    if (b.willExplode) this.explode(b.x, b.y, b.explodeRadius, b.damage);
    b.deactivate();
  }

  hitPlayer(b: EnemyBullet) {
    if (!b.active) return;
    this.deps.player.takeDamage(b.damage);
    if (b.willExplode) this.explode(b.x, b.y, b.explodeRadius, b.damage);
    b.deactivate();
  }

  // ── 每帧 ──────────────────────────────────────────────────

  /** 追踪弹制导（朝向玩家缓慢转向） */
  updateHoming(delta: number) {
    const p = this.deps.getPlayerPosition();
    this.deps.enemyBullets.getChildren().forEach((obj) => {
      const b = obj as EnemyBullet;
      if (!b.active || b.kind !== 'homing') return;
      const body = b.body as Phaser.Physics.Arcade.Body;
      const cur = Math.atan2(body.velocity.y, body.velocity.x);
      const want = Phaser.Math.Angle.Between(b.x, b.y, p.x, p.y);
      const next = Phaser.Math.Angle.RotateTo(cur, want, b.turnRate * (delta / 1000));
      const spd = body.velocity.length();
      body.setVelocity(Math.cos(next) * spd, Math.sin(next) * spd);
      b.setAngle(Phaser.Math.RadToDeg(next));
    });
  }

  /** 视野内唤醒 / 视野外休眠 + 驱动 AI */
  updateEnemies(time: number, delta: number, view: Phaser.Geom.Rectangle) {
    this.deps.enemies.getChildren().forEach((obj) => {
      if (!(obj instanceof Enemy) || !obj.active) return;
      const awake = view.contains(obj.x, obj.y);
      obj.setAwake(awake);
      if (awake) obj.updateAI(time, delta);
    });
  }

  clear() {
    this.deps.enemies.clear(true, true);
    this.deps.enemyBullets.clear(true, true);
  }
}
